import dayjs from 'dayjs'
import {
  TASK_TRACKER_RECURRING_TASK_MONTH_OPTIONS,
  TASK_TRACKER_RECURRING_TASK_TIME_MINUTE_STEP_MINUTES,
  TASK_TRACKER_RECURRING_TASK_WEEK_DAYS,
} from './taskTrackerRecurringTasks.constants'
import type {
  RecurringTaskFormValues,
  TaskTrackerRecurringTask,
  TaskTrackerRecurringTaskInterval,
  TaskTrackerRecurringTaskSavePayload,
} from './taskTrackerRecurringTasks.types'
import type { TaskTrackerBoardColumn } from '../store/taskTracker.types'
import type { TaskTrackerBoardFilterStickerRow } from '../store/domain.types'
import {
  mapRecurringTaskStickerDefaultsToFormRows,
  mapRecurringTaskStickerFormRowsToSavePayload,
  validateRecurringTaskStickerDefaults,
} from './taskTrackerRecurringTaskStickers.utils'

const DEFAULT_TIME = '12:00'
const MINUTES_IN_DAY = 24 * 60

export const snapRecurringTaskTimeToStep = (
  hours: number,
  minutes: number,
  stepMinutes = TASK_TRACKER_RECURRING_TASK_TIME_MINUTE_STEP_MINUTES,
): { hours: number; minutes: number } => {
  const totalMinutes = hours * 60 + minutes
  const snappedTotal =
    Math.round(totalMinutes / stepMinutes) * stepMinutes
  const normalized =
    ((snappedTotal % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY

  return {
    hours: Math.floor(normalized / 60),
    minutes: normalized % 60,
  }
}

export const parseRecurringTaskTimeParts = (
  time: string,
): { hours: number; minutes: number } => {
  const normalized = normalizeRecurringTaskTime(time)
  const [hoursRaw, minutesRaw] = normalized.split(':')

  return {
    hours: Number(hoursRaw),
    minutes: Number(minutesRaw),
  }
}

export const formatRecurringTaskTimeFromParts = (
  hours: number,
  minutes: number,
): string =>
  `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

export const normalizeRecurringTaskTime = (time: string): string => {
  const trimmed = time.trim()
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed)

  if (!match) return trimmed || DEFAULT_TIME

  const hours = Number(match[1])
  const minutes = Number(match[2])

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return DEFAULT_TIME
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return DEFAULT_TIME
  }

  const snapped = snapRecurringTaskTimeToStep(hours, minutes)
  return formatRecurringTaskTimeFromParts(snapped.hours, snapped.minutes)
}

export const resolveDefaultRecurringTaskColumnId = (
  columns: Array<Pick<TaskTrackerBoardColumn, 'id' | 'position'>>,
): number | null => {
  if (!columns.length) return null

  const sorted = [...columns].sort((a, b) => a.position - b.position)
  return sorted[0]?.id ?? null
}

export const getDefaultRecurringTaskStartDate = () => dayjs().format('YYYY-MM-DD')

export const parseRecurringTaskDate = (
  value: string | null | undefined,
): dayjs.Dayjs | null => {
  if (!value) return null

  const isoDate = dayjs(value, 'YYYY-MM-DD', true)
  if (isoDate.isValid()) return isoDate

  const dottedDate = dayjs(value, 'DD.MM.YYYY', true)
  return dottedDate.isValid() ? dottedDate : null
}

export const formatRecurringTaskDateLabel = (value: string | null | undefined) => {
  const parsed = parseRecurringTaskDate(value)
  return parsed ? parsed.format('DD.MM.YYYY') : ''
}

export const createEmptyRecurringTaskFormValues = (
  interval: TaskTrackerRecurringTaskInterval = 'daily',
  defaultColumnId: number | null = null,
): RecurringTaskFormValues => ({
  title: '',
  interval,
  time: DEFAULT_TIME,
  weekDays: [1],
  monthDay: 1,
  yearMonth: 1,
  yearDay: 1,
  startDate: getDefaultRecurringTaskStartDate(),
  endDate: '',
  isInfinite: true,
  columnId: defaultColumnId,
  stickerDefaults: [],
})

export const mapRecurringTaskToFormValues = (
  task: TaskTrackerRecurringTask,
): RecurringTaskFormValues => ({
  title: task.title,
  interval: task.interval,
  time: normalizeRecurringTaskTime(task.time),
  weekDays: task.weekDays.length > 0 ? [...task.weekDays] : [1],
  monthDay: task.monthDay ?? 1,
  yearMonth: task.yearMonth ?? 1,
  yearDay: task.yearDay ?? 1,
  startDate: task.startDate || getDefaultRecurringTaskStartDate(),
  endDate: task.endDate ?? '',
  isInfinite: task.endDate == null,
  columnId: task.columnId,
  stickerDefaults: mapRecurringTaskStickerDefaultsToFormRows(
    task.defaultStickerValues,
  ),
})

export const mapRecurringTaskFormToSavePayload = (
  values: RecurringTaskFormValues,
  stickerRows: TaskTrackerBoardFilterStickerRow[] = [],
): TaskTrackerRecurringTaskSavePayload => {
  const payload: TaskTrackerRecurringTaskSavePayload = {
    title: values.title.trim(),
    interval: values.interval,
    time: normalizeRecurringTaskTime(values.time),
    startDate: values.startDate,
    endDate: values.isInfinite ? null : values.endDate || null,
    columnId: values.columnId as number,
    defaultStickerValues: mapRecurringTaskStickerFormRowsToSavePayload(
      values.stickerDefaults,
      stickerRows,
    ),
  }

  if (values.interval === 'weekly') {
    payload.weekDays = [...values.weekDays].sort((a, b) => a - b)
  }

  if (values.interval === 'monthly') {
    payload.monthDay = values.monthDay
  }

  if (values.interval === 'yearly') {
    payload.yearMonth = values.yearMonth
    payload.yearDay = values.yearDay
  }

  return payload
}

const formatTimeLabel = (time: string) => time || DEFAULT_TIME

const formatWeekDaysLabel = (weekDays: number[]) => {
  const labels = weekDays
    .slice()
    .sort((a, b) => a - b)
    .map(
      (day) =>
        TASK_TRACKER_RECURRING_TASK_WEEK_DAYS.find((item) => item.value === day)
          ?.label ?? String(day),
    )

  return labels.join(', ')
}

const getMonthLabel = (month: number) =>
  TASK_TRACKER_RECURRING_TASK_MONTH_OPTIONS.find((item) => item.value === month)
    ?.label ?? String(month)

export const formatRecurringTaskScheduleLabel = (
  task: Pick<
    TaskTrackerRecurringTask,
    | 'interval'
    | 'time'
    | 'weekDays'
    | 'monthDay'
    | 'yearMonth'
    | 'yearDay'
  >,
): string => {
  const timeLabel = formatTimeLabel(task.time)

  switch (task.interval) {
    case 'daily':
      return `Ежедневно в ${timeLabel}`
    case 'weekly':
      return `${formatWeekDaysLabel(task.weekDays)} в ${timeLabel}`
    case 'monthly':
      return `Каждое ${task.monthDay ?? 1}-е число в ${timeLabel}`
    case 'yearly':
      return `${task.yearDay ?? 1} ${getMonthLabel(task.yearMonth ?? 1).toLowerCase()} в ${timeLabel}`
    default:
      return timeLabel
  }
}

export const formatRecurringTaskPeriodLabel = (
  task: Pick<TaskTrackerRecurringTask, 'startDate' | 'endDate'>,
): string => {
  const startLabel = formatRecurringTaskDateLabel(task.startDate)
  if (!startLabel) return ''

  if (task.endDate == null) {
    return `с ${startLabel}, бессрочно`
  }

  const endLabel = formatRecurringTaskDateLabel(task.endDate)
  return endLabel ? `с ${startLabel} по ${endLabel}` : `с ${startLabel}`
}

export const formatRecurringTaskColumnLabel = (
  columnId: number | null,
  columns: Array<Pick<TaskTrackerBoardColumn, 'id' | 'title'>>,
): string => {
  if (columnId == null) return 'Колонка не выбрана'

  const column = columns.find((item) => item.id === columnId)
  return column?.title?.trim() || `Колонка #${columnId}`
}

export const validateRecurringTaskForm = (
  values: RecurringTaskFormValues,
  options?: {
    stickerRows?: TaskTrackerBoardFilterStickerRow[]
  },
): string | null => {
  if (!values.title.trim()) {
    return 'Название регулярной задачи не должно быть пустым'
  }

  if (!/^\d{2}:\d{2}$/.test(normalizeRecurringTaskTime(values.time))) {
    return 'Укажите корректное время'
  }

  if (values.interval === 'weekly' && values.weekDays.length === 0) {
    return 'Выберите хотя бы один день недели'
  }

  if (
    values.interval === 'monthly' &&
    (values.monthDay < 1 || values.monthDay > 31)
  ) {
    return 'День месяца должен быть от 1 до 31'
  }

  if (values.interval === 'yearly') {
    if (values.yearMonth < 1 || values.yearMonth > 12) {
      return 'Укажите корректный месяц'
    }

    if (values.yearDay < 1 || values.yearDay > 31) {
      return 'Укажите корректный день'
    }
  }

  if (!parseRecurringTaskDate(values.startDate)) {
    return 'Укажите дату начала создания задач'
  }

  if (!values.isInfinite) {
    const endDate = parseRecurringTaskDate(values.endDate)
    const startDate = parseRecurringTaskDate(values.startDate)

    if (!endDate) {
      return 'Укажите дату окончания или отметьте «Бессрочно»'
    }

    if (startDate && endDate.isBefore(startDate, 'day')) {
      return 'Дата окончания не может быть раньше даты начала'
    }
  }

  if (values.columnId == null || values.columnId <= 0) {
    return 'Выберите колонку для создания задач'
  }

  if (values.stickerDefaults.length > 0) {
    const stickerError = validateRecurringTaskStickerDefaults(
      values.stickerDefaults,
      options?.stickerRows ?? [],
    )

    if (stickerError) return stickerError
  }

  return null
}
